const APPEARANCES = ["dark", "light"];

const COLOR_KEYS = [
  "backgroundCanvas",
  "backgroundSurface1",
  "backgroundSurface2",
  "backgroundSurface3",
  "backgroundOverlay",
  "backgroundInput",
  "textPrimary",
  "textSecondary",
  "textMuted",
  "textDisabled",
  "textInverse",
  "borderDefault",
  "borderStrong",
  "borderFocus",
  "success",
  "warning",
  "danger",
  "information",
  "brandPrimary",
  "brandHover",
  "brandPressed",
  "textOnBrand",
  "brandSubtle"
];

const dark = {
  appearance: "dark",
  color: {
    backgroundCanvas: "#1F1F1F",
    backgroundSurface1: "#292929",
    backgroundSurface2: "#323232",
    backgroundSurface3: "#3A3A3A",
    backgroundOverlay: "rgba(0, 0, 0, 0.72)",
    backgroundInput: "#212121",
    textPrimary: "#FFFFFF",
    textSecondary: "#E0E0E0",
    textMuted: "#BDBDBD",
    textDisabled: "#8A8A8A",
    textInverse: "#242424",
    borderDefault: "#4F4F4F",
    borderStrong: "#828282",
    borderFocus: "#FFFFFF",
    success: "#27AE60",
    warning: "#F5C451",
    danger: "#EB5757",
    information: "#62ABF5",
    brandPrimary: "#0F6CBD",
    brandHover: "#2886DE",
    brandPressed: "#115EA3",
    textOnBrand: "#FFFFFF",
    brandSubtle: "#26313B"
  }
};

const light = {
  appearance: "light",
  color: {
    backgroundCanvas: "#F5F5F5",
    backgroundSurface1: "#FFFFFF",
    backgroundSurface2: "#F0F0F0",
    backgroundSurface3: "#E5E5E5",
    backgroundOverlay: "rgba(0, 0, 0, 0.48)",
    backgroundInput: "#F5F5F5",
    textPrimary: "#242424",
    textSecondary: "#424242",
    textMuted: "#616161",
    textDisabled: "#A0A0A0",
    textInverse: "#FFFFFF",
    borderDefault: "#D1D1D1",
    borderStrong: "#8A8A8A",
    borderFocus: "#242424",
    success: "#27AE60",
    warning: "#8A6100",
    danger: "#EB5757",
    information: "#005FB8",
    brandPrimary: "#0F6CBD",
    brandHover: "#2886DE",
    brandPressed: "#115EA3",
    textOnBrand: "#242424",
    brandSubtle: "#E2EDF7"
  }
};

export const PRODUCT_THEME_DEFAULTS = Object.freeze({
  config: Object.freeze({ schemaVersion: 2 }),
  effectiveConfig: Object.freeze({
    schemaVersion: 2,
    appearance: Object.freeze({ default: "dark", allowed: Object.freeze([...APPEARANCES]) }),
    brand: Object.freeze({ primary: "#0F6CBD" })
  }),
  resolved: Object.freeze({ dark: Object.freeze(dark), light: Object.freeze(light) })
});

function hasFontField(value) {
  if (!value || typeof value !== "object") return false;
  return Object.entries(value).some(([key, nested]) => /font/i.test(key) || hasFontField(nested));
}

function isColorMap(value) {
  return Boolean(value) &&
    typeof value === "object" &&
    COLOR_KEYS.every(key => typeof value[key] === "string" && value[key].trim());
}

function isResolvedTheme(value, appearance) {
  return Boolean(value) && value.appearance === appearance && isColorMap(value.color);
}

function isConfig(value) {
  return Boolean(value) && typeof value === "object" && value.schemaVersion === 2 && !hasFontField(value);
}

function isValidTheme(theme) {
  if (!theme || typeof theme !== "object" || !isConfig(theme.config) || !isConfig(theme.effectiveConfig)) {
    return false;
  }

  const appearance = theme.effectiveConfig.appearance;
  if (!appearance || !APPEARANCES.includes(appearance.default) || !Array.isArray(appearance.allowed)) {
    return false;
  }

  const allowed = appearance.allowed;
  if (!allowed.length || new Set(allowed).size !== allowed.length || allowed.some(item => !APPEARANCES.includes(item))) {
    return false;
  }

  if (!allowed.includes(appearance.default) || !theme.resolved || typeof theme.resolved !== "object") {
    return false;
  }

  return Object.keys(theme.resolved).every(key => allowed.includes(key)) &&
    allowed.every(item => isResolvedTheme(theme.resolved[item], item));
}

export function normalizeResolvedThemeResponse(siteSettings) {
  const theme = siteSettings?.theme || siteSettings;
  return isValidTheme(theme) ? theme : PRODUCT_THEME_DEFAULTS;
}

export function getResolvedAppearance(themeResponse, appearance) {
  const theme = normalizeResolvedThemeResponse(themeResponse);
  return theme.resolved[appearance] || theme.resolved[theme.effectiveConfig.appearance.default];
}
